import { Link } from 'react-router-dom'
import { useVendorDirectory } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { DirectoryVendorCard } from '@/components/directory/DirectoryVendorCard'
import { DIRECTORY_SERVICES, EVENT_PAGE_VENDORS_PER_CATEGORY, type DirectoryService } from '@lib/constants/vendor-directory'
import { VENDOR_SERVICE_LABELS } from '@lib/enums/vendor-service.enum'
import type { EventDetailDto } from '@lib/dto/event.dto'
import { possessive } from '@lib/helpers/format'

/** What the event has someone for already: its assigned vendors' services, and planning when it has a planner. */
export function filledServices(event: EventDetailDto): Set<DirectoryService | 'furniture'> {
  const filled = new Set<DirectoryService | 'furniture'>(event.vendors.flatMap((v) => (v.connection === 'assigned' ? [v.service] : [])))
  if (event.planner !== null) filled.add('planning')
  return filled
}

/** A link to all of the venue's vendors, from the event page. */
export function BrowseVendorsLink({ eventId, venueName }: { eventId: string; venueName: string }) {
  return (
    <Link to={`/events/${eventId}/vendors`} className="inline-block text-sm underline underline-offset-2 hover:text-ink">
      Browse all of {possessive(venueName)} vendors
    </Link>
  )
}

/**
 * The couple's "Find vendors" on their event page (docs/vendor-growth.spec.md,
 * "Couples find vendors"): the venue's vendors, a few per category, for each
 * category the event has nobody for yet, in the venue's order.
 */
export function EventVendorsArea({ event }: { event: EventDetailDto }) {
  const directory = useVendorDirectory(event.id, { q: '' })
  const venueName = event.venue.name

  function groups() {
    if (directory.isPending) return <LoadingState label="Loading the venue’s vendors…" />
    if (directory.isError) return <ErrorState message={requestErrorMessage(directory.error)} onRetry={() => void directory.refetch()} />
    const filled = filledServices(event)
    const open = DIRECTORY_SERVICES.filter((service) => !filled.has(service))
      .map((service) => ({ service, vendors: directory.data.vendors.filter((v) => v.services.includes(service)) }))
      .filter((group) => group.vendors.length > 0)
    return open.map(({ service, vendors }) => (
      <div key={service} className="space-y-2">
        <h3 className="m-0 text-xs font-medium uppercase tracking-wide text-ink-muted">{VENDOR_SERVICE_LABELS[service]}</h3>
        <ul className="m-0 grid list-none gap-3 p-0 md:grid-cols-2">
          {vendors.slice(0, EVENT_PAGE_VENDORS_PER_CATEGORY).map((vendor) => (
            <DirectoryVendorCard key={vendor.vendorId} vendor={vendor} eventId={event.id} venueName={venueName} compact />
          ))}
        </ul>
        {vendors.length > EVENT_PAGE_VENDORS_PER_CATEGORY ? (
          <Link to={`/events/${event.id}/vendors?service=${service}`} className="inline-block text-sm underline underline-offset-2 hover:text-ink">
            See all {vendors.length}
          </Link>
        ) : null}
      </div>
    ))
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="m-0 text-sm font-medium">Find vendors</p>
        <p className="m-0 text-xs text-ink-muted">{possessive(venueName)} vendors for what your event still needs.</p>
      </div>
      {groups()}
      <BrowseVendorsLink eventId={event.id} venueName={venueName} />
    </div>
  )
}
